import { useState, useEffect, useRef } from 'react';

// Round to 2 decimal places using the same ROUND_HALF_UP logic as the backend
function round2(n) {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

function computeTotals(formData) {
  const items = formData.items || [];
  const discount = parseFloat(formData.discount) || 0;

  // Per-item amounts
  const itemsWithAmount = items.map(item => ({
    ...item,
    amount: round2((parseFloat(item.quantity) || 0) * (parseFloat(item.rate) || 0)),
  }));

  const subtotal = round2(itemsWithAmount.reduce((sum, item) => sum + item.amount, 0));
  const subtotalAfterDiscount = round2(subtotal - discount);

  let taxAmount = 0;
  let cgstAmount = 0;
  let sgstAmount = 0;
  let igstAmount = 0;

  // GST invoices have place_of_supply in formData; simple invoices do not
  const isGST = 'place_of_supply' in formData;

  if (isGST) {
    // is_same_state defaults to true (intra-state) unless explicitly false.
    // This matches the GST default: most sellers start within the same state.
    const isSameState = formData.is_same_state !== false;
    if (isSameState) {
      const cgstRate = parseFloat(formData.cgst_rate) || 0;
      const sgstRate = parseFloat(formData.sgst_rate) || 0;
      cgstAmount = round2(subtotalAfterDiscount * (cgstRate / 100));
      sgstAmount = round2(subtotalAfterDiscount * (sgstRate / 100));
      taxAmount = round2(cgstAmount + sgstAmount);
    } else {
      const igstRate = parseFloat(formData.igst_rate) || 0;
      igstAmount = round2(subtotalAfterDiscount * (igstRate / 100));
      taxAmount = igstAmount;
    }
  } else {
    const taxRate = parseFloat(formData.tax_rate) || 0;
    taxAmount = round2(subtotalAfterDiscount * (taxRate / 100));
  }

  const total = round2(subtotalAfterDiscount + taxAmount);

  return { itemsWithAmount, subtotal, taxAmount, cgstAmount, sgstAmount, igstAmount, total };
}

export function useCalculations(formData, setFormData, options = {}) {
  const { disabled = false } = options;

  // Kept for UI compatibility — always false since calculation is now synchronous
  const [isCalculating] = useState(false);

  // Serialize inputs to detect actual changes and skip no-op updates
  const prevInputsRef = useRef('');

  useEffect(() => {
    if (disabled || !formData || !setFormData) return;
    if (!formData.items || formData.items.length === 0) return;

    const inputsKey = JSON.stringify({
      items: formData.items.map(i => ({ q: i.quantity, r: i.rate })),
      discount: formData.discount,
      tax_rate: formData.tax_rate,
      cgst_rate: formData.cgst_rate,
      sgst_rate: formData.sgst_rate,
      igst_rate: formData.igst_rate,
      is_same_state: formData.is_same_state,
      place_of_supply: formData.place_of_supply,
    });

    if (inputsKey === prevInputsRef.current) return;
    prevInputsRef.current = inputsKey;

    const { itemsWithAmount, subtotal, taxAmount, cgstAmount, sgstAmount, igstAmount, total } =
      computeTotals(formData);

    setFormData(current => {
      const updates = {
        ...current,
        subtotal,
        total,
        tax_amount: taxAmount,
        items: current.items.map((item, i) =>
          itemsWithAmount[i] ? { ...item, amount: itemsWithAmount[i].amount } : item
        ),
      };

      // Only set GST breakdown fields when this is a GST invoice
      if ('place_of_supply' in current) {
        updates.cgst_amount = cgstAmount;
        updates.sgst_amount = sgstAmount;
        updates.igst_amount = igstAmount;
      }

      return updates;
    });
  }, [
    formData,
    disabled,
    setFormData,
  ]);

  const recalculate = () => {
    prevInputsRef.current = ''; // Force next effect run to recompute
  };

  return { recalculate, isCalculating };
}

export function useCalculatedTotals(formData) {
  return {
    subtotal: formData?.subtotal || 0,
    taxAmount: formData?.tax_amount || 0,
    total: formData?.total || 0,
  };
}

export default useCalculations;
